import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux'
import { Table, Button, Tag, Steps, Form, Typography, Dropdown, Input, DatePicker, message } from 'antd'
import {
    EditOutlined,
} from '@ant-design/icons';

// Config
import { APP_CONFIG } from '../../../../config'; 

// Constants
import { TEMPLATE } from '../../../../constants'

// Components
import { EmptyIndicator } from '../../../../components/Indicator'
import { PrimaryButton } from '../../../../components/Decoration/Button';
import ModalStandard from '../../../../components/ModalStandard';
import DrawerWrapper from '../../../../components/DrawerWrapper';
import Loading from '../../../../components/Loading'

// Services
import { TIME, REDUX } from '../../../../services/util'

// Redux Actions
import * as REDUX_ACTIONS from '../../../../services/redux/actions'

// Handler
import {
    table_handler,
} from '../../../../handlers'

// styles
import './index.css'

const { getColumnSearchProps } = table_handler
const { parseTime, parseDate, parseDuration } = TIME

const {
    STATUS_LABEL
} = TEMPLATE.TRACKING

const {
    FMS_V3_FE_LIVE_TRACKING_URL
} = APP_CONFIG

const LiveTrackingPanel = () => {

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [ form ] = Form.useForm()

    const { uid, name: uname } = useSelector(REDUX.reduxStoreWrapper('user'))
    const vehicleStaticses = useSelector(REDUX.reduxStoreWrapper('vehicles.staticsByID'))
    const locationTrackings = useSelector(REDUX.reduxStoreWrapper('locationTracking.byID'))
    const loading = useSelector(REDUX.reduxStoreWrapper('style.loading.liveTrackLink'))

    const plates = Object.values(vehicleStaticses).map(vehicle => vehicle.plate)

    const [ editedData, setEditedData ] = useState({})
    const [ isLoaded, setIsLoaded ] = useState(false);
    const searchTextState = useState('')
    const searchedColumnState = useState('')

    useEffect(() => {
        if(plates.length) {
            dispatch(REDUX_ACTIONS.v3_locationTracking.get_batch_location_tracking_by_plate_request(plates, 
                () => setIsLoaded(true)
            ))
            
        }
    }, [plates.length])

    const [now, setNow] = useState(Date.now())

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now())
        }, 10000)

        return () => clearInterval(interval)
    }, [])

    const dataSource = useMemo(() => {
        return Object.values(locationTrackings).map((tracking) => {
            const status = tracking.endTime < now && tracking.status === 1 ? 0 : tracking.status

            return {
                key: tracking.trackid,
                companyName: tracking.detail?.companyName,
                desc: tracking.desc || '-',
                url: `${FMS_V3_FE_LIVE_TRACKING_URL}livetracking?trackid=${tracking.trackid}`,
                status,
                duration: tracking.endTime - tracking.startTime,
                expiredIn: tracking.endTime > now ? tracking.endTime - now : 0,
                ...tracking,
            }
        })
    }, [locationTrackings, now])

    const saveDataHDLR = (trackid) => {

        const { status, plate, startTime, endTime } = locationTrackings[trackid] || {}
        const { lifetime, desc } = form.getFieldsValue()

        const formData = {
            ...locationTrackings[trackid],
            trackid,
            plate,
            status,
            startTime: TIME.parseTimestamp(lifetime[0]) || startTime || TIME.getCurTime(),
            endTime: TIME.parseTimestamp(lifetime[1]) || endTime || TIME.lastNDay(90),
            desc,
            uid
        }

        dispatch(REDUX_ACTIONS.v3_locationTracking.update_live_location_link_request(formData, () => {
            setEditedData({})
        }))

    }

    const onCancel = () => {
        form.resetFields()
        setEditedData({})
    }

    const vehiclePanelColumns = [
        {
            title: "Live Tracking Link",
            fixed: 'left',
            dataIndex: 'url',
            width: 350,
            ...getColumnSearchProps("Live Tracking Link", 'url', searchTextState, searchedColumnState),
            render: (data) => {

                return (
                    <Typography.Paragraph copyable={{ text: data }}>
                        <a href={data} target="_blank" rel="noopener noreferrer">
                            {data}
                        </a>
                    </Typography.Paragraph>
                )
            }
          },
          {
            title: "Status",
            dataIndex: "status",
            filters: Object.values(STATUS_LABEL).map(sl => ({
                text: <Tag color={STATUS_LABEL[sl.status]?.color}>{STATUS_LABEL[sl.status]?.label}</Tag>,
                value: sl.status
            })),
            onFilter: (value, record) => record.status === Number(value),
            render: (data) => 
                <Tag color={STATUS_LABEL[data]?.color}>
                    {/* <Badge color={STATUS_LABEL[data]?.color} style={{ marginRight: '8px'}}/> */}
                    {STATUS_LABEL[data]?.label}
                </Tag>,
          },
          {
            title: "Vehicle",
            dataIndex: "plate",
            ...getColumnSearchProps("Vehicle", 'plate', searchTextState, searchedColumnState),
            // filters: Object.values(vehicleStaticses).map(vehicle => ({
            //     text: vehicle.plate,
            //     value: vehicle.plate
            // })),
            // onFilter: (value, record) => record.plate === value,
          },
          {
            title: "Company Name",
            dataIndex: "companyName",
            width: 180,
            ...getColumnSearchProps("Company Name", 'companyName', searchTextState, searchedColumnState),
          },
          {
            title: "Tracking Lifetime",
            width: 240,
            ...getColumnSearchProps("Tracking Lifetime", 'lifetime', searchTextState, searchedColumnState),
            render: (data) => {

                const items = [
                    {
                        title: parseTime(Number(data.startTime)),
                    },
                    {
                        title: parseTime(Number(data.endTime)),
                    },
                ]

                return (
                    <Steps 
                        className='track-step'
                        progressDot
                        current={0}
                        direction="vertical"
                        items={items}
                    />
                )
            }
          },
        {
          title: "Duration",
          dataIndex: "duration",
          sorter: (a, b) => a.duration - b.duration,
          render: (data) => parseDuration(data)
        },
        {
          title: "Expired In",
          dataIndex: "expiredIn",
          sorter: (a, b) =>  a.expiredIn - b.expiredIn,
          render: (data) => data > 0 ? parseDuration(data) : '-'
        },
        {
          title: "Tracking Description",
          dataIndex: "desc",
          width: 200,
          ...getColumnSearchProps("Tracking Description", 'desc', searchTextState, searchedColumnState),
        },
        {
            title: "Last Updated By",
            defaultSortOrder: "descend",
            // dataIndex: 'updatedAt',
            width: 250,
            sorter: (a, b) => a.updatedAt - b.updatedAt,
            render: (data) => {
                const name = data.updatedBy === uid ? uname : data.updatedBy

                return (
                    <>
                        <>{name}</><br/>
                        <>{parseDate(Number(data.updatedAt))}</>
                    </>
                )
            }
          },
        {
            title: "Created By",
            width: 250,
            // dataIndex: 'createdAt',
            sorter: (a, b) => a.createdAt - b.createdAt,
            render: (data) => {
                const name = data.createdBy === uid ? uname : data.createdBy

                return (
                    <>
                        <>{name}</><br/>
                        <>{parseDate(Number(data.createdAt))}</>
                    </>
                )
            }
          },
        {
            title: "Actions",
            fixed: 'right',
            width: 160,
            render: data => {
                
                const items = [
                    {
                        key: '1',
                        label: 
                            <span 
                                className="ButtonLink"
                                aria-hidden="true"
                                onClick = {() => setEditedData(data)}
                            >
                                Edit
                            </span>
                    },
                    {
                        key: '2',
                        label: 
                            <span 
                                className="ButtonLink"
                                aria-hidden="true"
                                onClick={() => { 
                                    navigator.clipboard.writeText(data.url)
                                    message.success('Link copied!')
                                }}
                            >
                                Copy link
                            </span>
                    },
                    {
                        key: '3',
                        label: 
                        <ModalStandard 
                            onClick={() => {
                                dispatch(REDUX_ACTIONS.v3_locationTracking.update_live_location_link_request({ uid, ...data, status: 0 }))
                                
                            }}
                            disabled={data.status === 0}
                            title={'Mark as cancelled'}
                            content={'You are about to mark this live tracking link as cancelled.'}
                            subcontent={`${data.name || '-'}`}
                            // icon={<></>}
                        >
                            Mark as cancelled
                        </ModalStandard>
                    }
                ]

                return (
                    (data.status === 1 || data.status === 2)
                    &&
                    <Dropdown menu={{ items }}>
                        <EditOutlined />
                    </Dropdown>
                )

            }
          }
      ];


    const FormLiveTracking = (form, initialValues) => {

        // reset field
        useEffect(() => form.resetFields(), [initialValues])

        return (
            <Form
                name={'drawer_livetrack'}
                layout='vertical'
                form={form}
                initialValues={{
                    desc: initialValues.desc,
                    trackid: initialValues.trackid,
                    lifetime: [
                        TIME.dayJS(initialValues.startTime),
                        TIME.dayJS(initialValues.endTime)
                    ]
                }}
            >
                <Form.Item label='Live Tracking Link' >
                    <a href={initialValues.url} target="_blank" rel="noopener noreferrer">
                        {initialValues.url}
                    </a>
                </Form.Item>
                <Form.Item label='Vehicle Plate' >
                    {initialValues.plate}
                </Form.Item>

                <Form.Item label='Tracking lifetime' name={'lifetime'} >
                    <DatePicker.RangePicker
                        showTime={{ format: 'HH:mm' }}
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD HH:mm"
                        defaultValue={[TIME.getCurTimeDayJS()]}
                        disabledDate={(current) => current?.isBefore(TIME.getCurTimeDayJS().startOf("day"))}
                        disabledTime={(current) => {
                            const startDate = TIME.getCurTimeDayJS()
    
                            if (current && current.isSame(startDate, "day")) {
                                const currentHour = startDate.hour()
                                const currentMinute = startDate.minute()

                                return {
                                    disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(hour => hour < currentHour),
                                    disabledMinutes: (selectedHour) => (selectedHour === currentHour) ? Array.from({ length: 60 }, (_, i) => i).filter(minute => minute < currentMinute) : []
                                };
                            }
                            return {};
                        }
                    }
                    />
                </Form.Item>
                <Form.Item label='Tracking description' name={'desc'} >
                    <Input />
                </Form.Item>
            </Form>
        )
    }

    const DrawerFooter = ({ trackid }) => (
        <div className={'v-center'} style={{ gap: '1em' }}>
            <PrimaryButton style={{ width: '40%'}} onClick={() => saveDataHDLR(trackid)}>Save</PrimaryButton>
            <Button style={{ width: '40%'}} onClick={onCancel}>Cancel</Button>
        </div>
    )

    const nEdit = Object.keys(editedData).length > 1

    return (
        <div >
            <PrimaryButton htmlType={'submit'} style={{ float: 'right', marginBottom: '15px' }} onClick={() => navigate (`/create/link/livetrack`)}>+ Create live location link</PrimaryButton>
            {   loading && !isLoaded ? 
                <Loading /> :
                dataSource.length > 0 ?
                    <Table
                        loading={loading}
                        columns={vehiclePanelColumns && vehiclePanelColumns.filter(c => !c.isHidden)}
                        dataSource={dataSource}
                        pagination={true}
                        scroll={{
                            y: window.innerHeight,
                            x: vehiclePanelColumns && vehiclePanelColumns.length * 200
                        }}
                    /> 
                :
                    <EmptyIndicator />
            }
            <DrawerWrapper
                title={`Edit Live Tracking Link`}
                open={nEdit}
                onClose={onCancel}
                footer={<DrawerFooter trackid={editedData.trackid} />}
            >
                { FormLiveTracking(form, editedData) }
            </DrawerWrapper>
            
        </div>
    )
}

export default LiveTrackingPanel